import { SkillDto } from '@app/core/model/skill-dto';
import { SkillAssignmentCore } from '@app/core/model/skill-assignment-core';
import { ExperienceEnum } from '@app/_api/enums/experience.enum';
import { TreeCanvasUtil } from '@app/pages/home/tree/tree-canvas/utils/tree-canvas.util';
import { UISkill } from '@app/pages/home/tree/tree-canvas/tree-types';
import { TreeColor } from '@app/pages/home/tree/tree-canvas/utils/tree-colors';
import { UUID } from '@app/_api/identifyable';

export class VisibleSkillMapperUtil extends TreeCanvasUtil {
  public static readonly skillsWithExperienceLevelAndColor = (
    skills: SkillDto[],
    skillAssignments: Record<UUID, SkillAssignmentCore>,
    firstLevelSkills: SkillDto[],
    allSkills: SkillDto[],
  ): UISkill[] => {
    return skills.map(skill => ({
      ...skill,
      color: this.getColor(skill, skillAssignments, firstLevelSkills, allSkills),
      experienceLevel: this.getExperienceLevel(skill.id, skillAssignments),
    }));
  };

  public static readonly getExperienceLevel = (skillId: UUID, skillAssignments: Record<UUID, SkillAssignmentCore>): ExperienceEnum => {
    return skillAssignments[skillId]?.experience ?? ExperienceEnum.NONE;
  };

  public static readonly findFirstLevelAncestorId = (
    allSkills: SkillDto[],
    firstLevelSkills: SkillDto[],
    startParentId: string | null,
  ): string | undefined => {
    let current = allSkills.find(s => s.id === startParentId);

    while (current && !firstLevelSkills.some(f => f.id === current?.id)) {
      current = allSkills.find(s => s.id === current?.parentId);
    }

    return current?.id;
  };

  public static readonly getColor = (
    skill: SkillDto,
    skillAssignments: Record<UUID, SkillAssignmentCore>,
    firstLevelSkills: SkillDto[],
    allSkills: SkillDto[],
  ): TreeColor => {
    if (this.getExperienceLevel(skill.id, skillAssignments) === ExperienceEnum.NONE) {
      return this.UNASSIGNED_SKILLS_STROKE_COLOR;
    }

    const firstLevelSkillId = this.findFirstLevelSkillId(skill, firstLevelSkills, allSkills);
    const matchingFirstLevelIndex = firstLevelSkills.findIndex(f => f.id === firstLevelSkillId);

    return matchingFirstLevelIndex >= 0 ? this.colors[matchingFirstLevelIndex % this.colors.length] : this.UNASSIGNED_SKILLS_STROKE_COLOR;
  };

  private static findFirstLevelSkillId(skill: SkillDto, firstLevelSkills: SkillDto[], allSkills: SkillDto[]): string | undefined {
    return firstLevelSkills.find(f => f.id === skill.id)?.id ?? this.findFirstLevelAncestorId(allSkills, firstLevelSkills, skill.parentId!);
  }
}
